import { useCallback, useState } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ResolutionStatus from '../../components/ResolutionStatus/ResolutionStatus';
import ResolutionDialog from '../../components/ResolutionDialog/ResolutionDialog';
import EmployeeResolutionViewer from '../../components/EmployeeResolutionViewer/EmployeeResolutionViewer';
import { Cell } from '../../components/Cell';

const ResolutionCell = ({
  resolution,
  status,
  requestId,
  requestedBy,
  vacationFrom,
  vacationTo,
  onGenerateResolutionAction,
  onSendResolutionAction,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isViewerOpen, setViewerOpen] = useState(false);
  const [registryNumber, setRegistryNumber] = useState('');

  const onPreview = useCallback(
    async ({ registryNumber: regNum }) => {
      setIsProcessing(true);
      try {
        return await onGenerateResolutionAction({ requestId, registryNumber: regNum });
      } catch {
        enqueueSnackbar('Error when generating resolution preview', { variant: 'error' });
        throw new Error('Preview generation failed');
      } finally {
        setIsProcessing(false);
      }
    },
    [onGenerateResolutionAction, requestId, enqueueSnackbar],
  );

  const onSend = useCallback(async () => {
    setIsProcessing(true);
    try {
      await onSendResolutionAction({ requestId, registryNumber });
      enqueueSnackbar('Resolution successfully issued and sent', { variant: 'success' });
      setDialogOpen(false);
      setRegistryNumber('');
    } catch {
      enqueueSnackbar('Error when sending resolution', { variant: 'error' });
    } finally {
      setIsProcessing(false);
    }
  }, [onSendResolutionAction, requestId, registryNumber, enqueueSnackbar]);

  return (
    <>
      <Cell>
        <ResolutionStatus resolution={resolution} />
      </Cell>
      <Cell>
        {status === 'approved' && (
          <Button
            variant="contained"
            size="small"
            disabled={isProcessing}
            onClick={() => {
              setRegistryNumber(resolution?.registryNumber ?? '');
              setDialogOpen(true);
            }}
            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, minWidth: 76 }}
            data-cy="issueResolutionButton"
          >
            {resolution?.mailDocId ? 'Re-issue' : 'Issue'}
          </Button>
        )}
      </Cell>
      <Cell>
        {resolution?.mailDocId && (
          <Tooltip title="View document">
            <IconButton
              size="small"
              sx={{ p: 0 }}
              onClick={() => setViewerOpen(true)}
              data-cy="previewSentResolutionButton0"
            >
              <PictureAsPdf
                fontSize="small"
                sx={resolution?.accessLog?.length > 0 ? { color: 'success.main' } : undefined}
              />
            </IconButton>
          </Tooltip>
        )}
      </Cell>
      <ResolutionDialog
        isDialogOpen={isDialogOpen}
        setDialogOpen={setDialogOpen}
        requestedBy={requestedBy}
        vacationFrom={vacationFrom}
        vacationTo={vacationTo}
        registryNumber={registryNumber}
        setRegistryNumber={setRegistryNumber}
        onPreview={onPreview}
        onSend={onSend}
        isProcessing={isProcessing}
        isReissue={!!resolution?.mailDocId}
      />
      <EmployeeResolutionViewer
        open={isViewerOpen}
        onClose={() => setViewerOpen(false)}
        storagePaths={resolution?.pdfs?.map(pdf => pdf.storagePath) ?? []}
      />
    </>
  );
};

export default ResolutionCell;
